/**
 * Google rating + reviews on the shop (worker/src/shop-reviews.ts). Everything here renders nothing
 * when `GET /api/shop/reviews` answers null (no confirmed listing, no key, or the copy is older than
 * the allowed cache) — never a stale or empty widget.
 *
 * Attribution follows Google's Places policy: "Reviews from Google", every author credited with
 * avatar + name linked to their profile, each review linked to its source on Google Maps, and a note
 * on how reviews are chosen. 🔴 No aggregateRating / Review structured data is emitted from this
 * (self-serving third-party reviews break Google's structured-data policy): visible text only.
 */
import { useEffect, useState } from "react";
import { get } from "./shared";

export interface GoogleReviewView {
  author: string;
  authorUri: string | null;
  authorPhoto: string | null;
  rating: number;
  relativeTime: string;
  text: string;
  mapsUri: string;
}
export interface ReviewsAnswer {
  rating: number;
  count: number;
  reviews: GoogleReviewView[];
  mapsUrl: string;
  fetchedAt: string;
}

const EXT = { target: "_blank", rel: "noopener noreferrer" } as const;
/** One request per visit, shared by the header, the product page and the home section. */
let pending: Promise<ReviewsAnswer | null> | null = null;

export function useReviews(): ReviewsAnswer | null {
  const [r, setR] = useState<ReviewsAnswer | null>(null);
  useEffect(() => {
    let live = true;
    pending ??= get<ReviewsAnswer | null>("/shop/reviews").catch(() => null);
    void pending.then((v) => { if (live) setR(v && typeof v.rating === "number" && v.count > 0 ? v : null); });
    return () => { live = false; };
  }, []);
  return r;
}

const Stars = ({ n }: { n: number }) => (
  <span className="g-stars" role="img" aria-label={`${n} out of 5 stars`}>{"★".repeat(n)}<i>{"★".repeat(5 - n)}</i></span>
);

/** "4.9 ★ · 18 Google reviews", linking to the listing on Google Maps. */
export function ReviewBadge({ r, className }: { r: ReviewsAnswer; className?: string }) {
  return (
    <a className={`g-badge${className ? ` ${className}` : ""}`} href={r.mapsUrl} {...EXT}>
      <b>{r.rating.toFixed(1)}</b> <span aria-hidden="true">★</span> · {r.count} Google {r.count === 1 ? "review" : "reviews"}
      <span className="sr-only"> (opens Google Maps in a new tab)</span>
    </a>
  );
}

export function ReviewsSection({ r }: { r: ReviewsAnswer }) {
  if (r.reviews.length === 0) return null;
  return (
    <section className="wrap home-sec g-reviews" aria-labelledby="h-reviews">
      <div className="sec-head">
        <h2 id="h-reviews">What customers say</h2>
        <a className="sec-link" href={r.mapsUrl} {...EXT}>See all on Google Maps →<span className="sr-only"> (opens in a new tab)</span></a>
      </div>
      <p className="g-source">Reviews from Google · <b>{r.rating.toFixed(1)}</b> ★ from {r.count} reviews</p>
      <ul className="g-list">
        {r.reviews.map((v, i) => (
          <li key={i} className="g-card">
            <div className="g-who">
              {v.authorPhoto && <img src={v.authorPhoto} alt="" width={40} height={40} loading="lazy" referrerPolicy="no-referrer" />}
              <span>
                {v.authorUri ? <a href={v.authorUri} {...EXT}>{v.author}</a> : <b>{v.author}</b>}
                <small>{v.relativeTime}</small>
              </span>
            </div>
            <Stars n={v.rating} />
            <p>{v.text}</p>
            <a className="g-src" href={v.mapsUri} {...EXT}>View on Google Maps<span className="sr-only"> (opens in a new tab)</span></a>
          </li>
        ))}
      </ul>
      <p className="g-note">Google Maps shows its most relevant reviews first; these are the ones it returned to us, not a selection of ours.</p>
    </section>
  );
}
